import { AuthError } from "@vidario/api-client";
import { Button, Input, VidarioLogo } from "@vidario/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState, type ReactElement } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router";
import { z } from "zod";

import { useAuth } from "../../providers/auth-provider";

const schema = z
  .object({
    password: z.string().min(8, "Mindestens 8 Zeichen."),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    path: ["confirm"],
    message: "Die Passwörter stimmen nicht überein.",
  });
type ResetInput = z.infer<typeof schema>;

/** Landing page for the link inside the password-reset email
 * (`/reset-password?uid=…&token=…`). The auth client's
 * `updatePassword` pulls uid + token straight from the URL query string,
 * so we don't have to thread them through React state — just call it
 * with the new password. */
export function ResetPasswordRoute(): ReactElement {
  const { updatePassword } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [done, setDone] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // An incomplete link can't work either; both get the same way out (#21).
  const [linkExpired, setLinkExpired] = useState(!params.get("uid") || !params.get("token"));

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetInput>({
    resolver: zodResolver(schema),
    defaultValues: { password: "", confirm: "" },
  });

  const onSubmit = async (values: ResetInput): Promise<void> => {
    setSubmitError(null);
    try {
      await updatePassword(values.password);
      setDone(true);
    } catch (err) {
      if (err instanceof AuthError && err.code === "link_expired") {
        setLinkExpired(true);
        return;
      }
      // Stays until the next attempt: the password rules need reading (#21).
      setSubmitError(
        err instanceof Error && err.message
          ? err.message
          : "Passwort konnte nicht gesetzt werden. Bitte fordere einen neuen Link an.",
      );
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg-app px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-center">
          <VidarioLogo size={36} />
        </div>
        <div className="rounded-lg border border-border-default bg-neutral-0 p-8 shadow-sm">
          <h1 className="m-0 text-2xl font-semibold -tracking-[0.3px]">Neues Passwort setzen</h1>
          <p className="mt-1 mb-6 text-sm text-text-secondary">
            Wähle ein neues Passwort. Mindestens 8 Zeichen.
          </p>

          {done ? (
            <div
              role="status"
              className="rounded-md border border-success-200 bg-success-50 px-4 py-3 text-sm text-success-700"
              data-testid="reset-success"
            >
              Dein Passwort wurde aktualisiert. Du kannst dich jetzt anmelden.
            </div>
          ) : linkExpired ? (
            <div className="flex flex-col gap-4">
              <div
                role="alert"
                className="rounded-md border border-danger-500 bg-danger-50 px-4 py-3 text-sm text-danger-700"
                data-testid="link-expired"
              >
                <p className="m-0 font-medium">Link abgelaufen</p>
                <p className="m-0 mt-1">
                  Dieser Link ist abgelaufen oder ungültig. Fordere einen neuen an, um dein
                  Passwort zu setzen.
                </p>
              </div>
              <Button onClick={() => void navigate("/forgot-password")}>Neuen Link anfordern</Button>
            </div>
          ) : (
            <form
              onSubmit={(e) => void handleSubmit(onSubmit)(e)}
              className="flex flex-col gap-4"
              noValidate
            >
              <Input
                label="Neues Passwort"
                type="password"
                autoComplete="new-password"
                required
                {...register("password")}
                {...(errors.password?.message && { error: errors.password.message })}
              />
              <Input
                label="Passwort wiederholen"
                type="password"
                autoComplete="new-password"
                required
                {...register("confirm")}
                {...(errors.confirm?.message && { error: errors.confirm.message })}
              />
              {submitError && (
                <p role="alert" className="text-sm text-danger-600">
                  {submitError}
                </p>
              )}
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                Passwort speichern
              </Button>
            </form>
          )}

          <div className="mt-5 text-center text-sm">
            <Link
              to="/login"
              className="font-medium text-primary-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              Zurück zur Anmeldung
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
